import {Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {DetailsConfig, SharedUiDetailsModule} from '@snelstart/ui-details';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {filter, Observable, Subject, Subscriber, takeUntil} from "rxjs";
import {
  BezwaarBelastingmiddel,
  BezwaarEntity, Bezwaarschrift, BezwaarSoort, BezwaarSpecificatie,
  BokkzAlgDataService,
  BokkzBezwaarClient,
  BokkzBezwaarDataService,
  ClientEntity, ContactPersoon, Dienstverlener,
  IntermediaryEntity, PdfRequest, PdfRequestOptie, PdfRequestSoort, PdfRequestType
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ToastService} from "@snelstart/ui-toast";
import {BokkzClient} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {Event as Event_2, NavigationEnd, Router} from "@angular/router";
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {BezwaarComponent} from '../bezwaar/bezwaar.component';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {CommonModule, DecimalPipe, NgClass} from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-bezwaar-details',
  templateUrl: './feature-tax-bezwaar-details.component.html',
  styleUrl: './feature-tax-bezwaar-details.component.scss',
  imports: [
    SharedUiButtonModule,
    SharedUiDialogModule,
    SharedUiAlertModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    BezwaarComponent,
    SharedUiIconModule,
    NgClass,
    DecimalPipe,
    CommonModule
  ]
})
export class FeatureTaxBezwaarDetailsComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  @Output() public dialogVisibleChange = new EventEmitter<boolean>();

  public clientId = {} as any;
  public intermediairyEntity = {} as IntermediaryEntity;
  private isSaving = false;
  public clientEntity = {} as ClientEntity;
  public bezwaarEntity = {} as BezwaarEntity;
  private destroy$ = new Subject<void>();
  public dialogGegevensVisible = false;
  public dialogVerzendenVisible = false;
  public dialogAfdrukkenVisible = false;
  public formComplete = false;
  public fileName = '';
  public uploadSuccesvol = true;
  public bezwaarschriftFg = {} as FormGroup;
  public bezwaarSpecificatieFg = {} as FormGroup;
  public contactFg = {} as FormGroup;

  protected readonly detailsConfig = DetailsConfig.create({
    hideArrowButtons: true,
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {label: "Ja", value: true, disabled: false},
    {label: "Nee", value: false, disabled: false},
  ];

  public optionsJaartal: SelectFormControlItem<string>[] = [
    {value: '2025', label: '2025'},
    {value: '2024', label: '2024'},
    {value: '2023', label: '2023'},
    {value: '2022', label: '2022'},
    {value: '2021', label: '2021'},
  ];

  public onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    this.fileName = file.name;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];

      const bezwaarSpec = this.bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie;
      if (!bezwaarSpec) return;

      if (!Array.isArray(bezwaarSpec.bijlagen)) {
        bezwaarSpec.bijlagen = [];
      }

      const estimatedFileSizeBytes = (base64.length * 3) / 4;
      const currentTotalBytes = this.getTotalBase64SizeInBytes();
      const newTotalMB = (currentTotalBytes + estimatedFileSizeBytes) / (1024 * 1024);

      if (newTotalMB > 15) {
        alert('Totaal van de bijlagen mag niet groter zijn dan 15 Mb!');
        this.uploadSuccesvol = false;
        return;
      }

      bezwaarSpec.bijlagen.push({
        filename: file.name,
        content: base64,
      });

      this.uploadSuccesvol = true;
    };

    reader.onerror = () => {
      console.error('Fout bij lezen bestand');
      this.uploadSuccesvol = false;
    };

    reader.readAsDataURL(file);
  }

  buttonConfig = this.createIndienenButtonConfig(true); // of false

  public createIndienenButtonConfig(sendToAuthorities: boolean): ButtonConfiguration {
    return ButtonConfiguration.create({
      onClick: () =>
        new Observable<void>((subscription) => {
          this.sendForm(subscription, sendToAuthorities);
        }),
    });
  }

  public readonly afdrukkenButtonConfig = ButtonConfiguration.create({
    onClick: () =>
      new Observable((subscriber) => {
        setTimeout(() => {
          this.dialogVisibleChange.emit(false);
          subscriber.next(true);
          subscriber.complete();
        }, 4000);
      }),
  });

  public showDialogGegevens() {
    this.dialogGegevensVisible = true;
  }

  public showDialogVerzenden() {
    this.checkCompletion()
    this.dialogVerzendenVisible = true;
  }

  public showDialogAfdrukken() {
    this.checkCompletion()
    this.dialogAfdrukkenVisible = true;
  }

  constructor(
    private injector: Injector,
    public algDataService: BokkzAlgDataService,
    public dataService: BokkzBezwaarDataService,
    public bokkzClient: BokkzBezwaarClient,
    private router: Router,
    public toastService: ToastService,
    public fb: FormBuilder,
  ) {
    this.bezwaarschriftFg = this.fb.group<Bezwaarschrift>({
      naamBelanghebbende: null as unknown as string,
      bsnBelanghebbende: null as unknown as string,
      contactPersoon: null as unknown as ContactPersoon,
      intermediair: null as unknown as Dienstverlener,
      bezwaarSpecificatie: null as unknown as BezwaarSpecificatie,
    })
    this.bezwaarSpecificatieFg = this.fb.group<BezwaarSpecificatie>({
      dagtekening: null as unknown as Date,
      aanslagnummer: null as unknown as string,
      aanslagJaar: null as unknown as string,
      belastingMiddel: null as unknown as BezwaarBelastingmiddel,
      bezwaarBox3: null as unknown as boolean,
      bezwaarOndernemerschapIh: null as unknown as boolean,
      motivatie: null as unknown as string,
      bezwaarSoort: null as unknown as BezwaarSoort,
      bijlagen: null as unknown as []
    })
    this.contactFg = this.fb.group<ContactPersoon>({
      voorletters: null as unknown as string,
      tussenvoegsel: null as unknown as string,
      achternaam: null as unknown as string,
      emailadres: null as unknown as string,
      telefoonnummer: null as unknown as string,
    })
  }

  ngOnInit(): void {
    this.algDataService.client.pipe(takeUntil(this.destroy$)).subscribe(value => {
        this.clientEntity = value
        this.clientId=value.sstRelationId;
    });
    this.algDataService.intermediary.pipe(takeUntil(this.destroy$)).subscribe(value => this.intermediairyEntity = value);
    this.dataService.bezwaarEntity
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.bezwaarEntity = value;
        if (value.bezwaarschrift && value.bezwaarschrift.bezwaarSpecificatie) {
          this.bezwaarschriftFg.reset()
          this.bezwaarSpecificatieFg.reset()
          this.bezwaarschriftFg.patchValue(value.bezwaarschrift);
          this.bezwaarSpecificatieFg.patchValue(value.bezwaarschrift?.bezwaarSpecificatie);
        }
        if (value.bezwaarschrift && value.bezwaarschrift.contactPersoon) {
          this.contactFg.patchValue(value.bezwaarschrift.contactPersoon)
        }
      });
    this.router.events.pipe((takeUntil(this.destroy$)), filter(
      (event: Event_2): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleUrlChange(event.urlAfterRedirects);
      });
  }

  handleUrlChange(newUrl: string) {
    if (!this.isSaving && this.hasUnsavedChanges()) {
      this.isSaving = true;
      this.bokkzClient.postOneBezwaar(this.clientEntity.sstRelationId!, false, this.bezwaarEntity).subscribe((bezwaarEntity) => {
        this.dataService.updateItem(bezwaarEntity);
      });
      this.isSaving = false;
    }
  }

  private hasUnsavedChanges(): boolean {
    return this.bezwaarschriftFg.dirty || this.bezwaarSpecificatieFg.dirty || this.contactFg.dirty;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  configSave(sendToAuthorities: boolean) {
    return ButtonConfiguration.create({
      onClick: () =>
        new Observable((subscription) => {
          this.sendForm(subscription, sendToAuthorities);
        }),
    })
  }

  getTitle() {
    const spec = this.bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie;
    const middel = spec?.belastingMiddel?.includes('IH') ? 'inkomstenbelasting ' :
      (spec?.belastingMiddel?.includes('VPB') ? 'vennootschapsbelasting ' :
        (spec?.belastingMiddel?.includes('OB') ? 'omzetbelasting ' : ''))
    const jaar = spec?.aanslagJaar != null ? spec?.aanslagJaar : ''
    const soort = spec?.bezwaarSoort?.includes('ASLG') ? 'de aanslag ' :
      (spec?.bezwaarSoort?.includes('BBB') ? 'de boetebeschikking ' :
          (spec?.bezwaarSoort?.includes('NHA') ? 'de naheffingsaanslag ' :
              (spec?.bezwaarSoort?.includes('VLBS') ? 'de verliesbeschikking ' :
                  (spec?.bezwaarSoort?.includes('AMBA') ? 'de ambtshalve aanslag ' :
                      (spec?.bezwaarSoort?.includes('NVAS') ? 'de navorderingsaanslag ' :
                          (spec?.bezwaarSoort?.includes('BLSR') ? 'de beschikking belastingrente ' :
                            '....')
                      )
                  )
              )
          )
      )
    return "tegen " + soort + middel + jaar
  }

  getDossier() {
    const spec = this.bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie;
    const middel = spec?.belastingMiddel?.includes('IH') ? 'inkomstenbelasting ' :
      (spec?.belastingMiddel?.includes('VPB') ? 'vennootschapsbelasting ' :
        (spec?.belastingMiddel?.includes('OB') ? 'omzetbelasting ' : ''))
    const jaar = spec?.aanslagJaar != null ? spec?.aanslagJaar : ''
    return middel + jaar
  }

  isDagtekeningOuderDanZesWeken(): boolean {
    const dagtekening: Date = this.bezwaarSpecificatieFg.get('dagtekening')?.value;
    if (!dagtekening) {
      return false;
    }

    const zesWekenGeleden = new Date();
    zesWekenGeleden.setDate(zesWekenGeleden.getDate() - 42); // 6 weken = 42 dagen
    return new Date(dagtekening) < zesWekenGeleden;
  }

  public checkCompletion() {
    let formIsValid = this.bezwaarSpecificatieFg.get('dagtekening')?.value !== null && this.bezwaarSpecificatieFg.get('dagtekening')?.value !== ""
      && this.bezwaarSpecificatieFg.get('aanslagnummer')?.value !== null && this.bezwaarSpecificatieFg.get('aanslagnummer')?.value !== ""
      && this.bezwaarSpecificatieFg.get('aanslagJaar')?.value !== null && this.bezwaarSpecificatieFg.get('aanslagJaar')?.value !== ""
      && this.bezwaarSpecificatieFg.get('belastingMiddel')?.value !== null && this.bezwaarSpecificatieFg.get('belastingMiddel')?.value !== ""
      && this.bezwaarSpecificatieFg.get('bezwaarSoort')?.value !== null && this.bezwaarSpecificatieFg.get('bezwaarSoort')?.value !== ""
      && this.bezwaarschriftFg.get('naamBelanghebbende')?.value !== null && this.bezwaarschriftFg.get('naamBelanghebbende')?.value !== ""
      && this.bezwaarschriftFg.get('bsnBelanghebbende')?.value !== null && this.bezwaarschriftFg.get('bsnBelanghebbende')?.value !== ""
      && this.contactFg.get('voorletters')?.value !== null && this.contactFg.get('voorletters')?.value !== ""
      && this.contactFg.get('achternaam')?.value !== null && this.contactFg.get('achternaam')?.value !== ""
      && this.contactFg.get('emailadres')?.value !== null && this.contactFg.get('emailadres')?.value !== ""
      && this.contactFg.get('telefoonnummer')?.value !== null && this.contactFg.get('telefoonnummer')?.value !== "";

    if (this.bezwaarSpecificatieFg.get('belastingMiddel')?.value === 'IH') {
      formIsValid = formIsValid &&
        this.bezwaarSpecificatieFg.get('bezwaarBox3')?.value !== null && this.bezwaarSpecificatieFg.get('bezwaarBox3')?.value !== "null" &&
        this.bezwaarSpecificatieFg.get('bezwaarOndernemerschapIh')?.value !== null && this.bezwaarSpecificatieFg.get('bezwaarOndernemerschapIh')?.value !== "";
    }
    this.formComplete = formIsValid;
  }

  public saveForm() {
    this.bezwaarschriftFg.updateValueAndValidity({ emitEvent: false });
    this.bezwaarSpecificatieFg.updateValueAndValidity({ emitEvent: false });
    this.contactFg.updateValueAndValidity({ emitEvent: false });

    this.bezwaarEntity = {
      ...this.bezwaarEntity,
      bezwaarschrift: {
        ...this.bezwaarEntity.bezwaarschrift,
        contactPersoon: {
          ...this.bezwaarEntity.bezwaarschrift?.contactPersoon,
          ...this.contactFg.getRawValue()
        },
        bezwaarSpecificatie: {
          ...this.bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie,
          ...this.bezwaarSpecificatieFg.getRawValue()
        }
      }
    };

    this.dataService.updateItem(this.bezwaarEntity);
  }

  sendForm(subscription: Subscriber<void>, sendToAuthorities: boolean) {
    this.bokkzClient.postOneBezwaar(String(this.clientEntity.sstRelationId), sendToAuthorities, this.bezwaarEntity).subscribe({
      next: (item) => {
        this.dataService.updateItem(item);
        subscription.next();
        subscription.complete();
      },
      error: () => {
        subscription.next();
        subscription.complete();
        console.log('error');
      },
      complete: () => {
        if (sendToAuthorities) {
          this.toastService.showSuccessMessage("Het bezwaar is succesvol verzonden naar de Belastingdienst.");
        } else {
          this.toastService.showSuccessMessage("Het bezwaar is succesvol opgeslagen.");
        }
        this.dialogVisibleChange.emit(false);
      }
    })
  }

  getTotalBase64SizeInBytes(): number {
      const bijlagen = this.bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie?.bijlagen || [];

      const totalBytes = bijlagen.reduce((total, bijlage) => {
          if (!bijlage?.content) return total;

          const padding = (bijlage.content.match(/=+$/) || [''])[0].length;
          const sizeInBytes = (bijlage.content.length * 3 / 4) - padding;

          return total + sizeInBytes;
      }, 0);

      // Buffer toegevoegd van een half mb.
      const bufferBytes = 0.5 * 1024 * 1024;

      return totalBytes + bufferBytes;
  }

  getTotalBase64SizeInMB(): number {
    return this.getTotalBase64SizeInBytes() / (1024 * 1024);
  }

  public createPdf() {
    const pdf = {} as PdfRequest;
    this.genereerPdfBezwaar(pdf);
  }

  private genereerPdfBezwaar(pdf: PdfRequest) {
    const bokkzClient = this.injector.get(BokkzClient);
    pdf.bezwaarschrift = this.bezwaarEntity.bezwaarschrift as unknown as Bezwaarschrift;
    pdf.type = PdfRequestType.BZW;
    pdf.soort = PdfRequestSoort.Bezwaar;
    pdf.optie = PdfRequestOptie.GeheleRapport;
    pdf.concept = true;
    pdf.time = new Date().toISOString();

    const pdfAntwoord = bokkzClient.getPdfRapport(pdf);
    const currentDate = new Date();
    const day = currentDate.getDate().toString().padStart(2, '0');
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const year = currentDate.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;
    const naam = this.bezwaarEntity.bezwaarschrift?.naamBelanghebbende || 'onbekend';
    const aanslagjaar = this.bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie?.aanslagJaar || 'onbekend';
    const aanslagnummer = this.bezwaarEntity.bezwaarschrift?.bezwaarSpecificatie?.aanslagnummer || 'onbekend';

    pdfAntwoord.subscribe({
      next: (pdfStringBase64) => {
        let blob = {} as Blob;
        try {
          blob = this.dataURItoBlob(pdfStringBase64);
        } catch (error) {
          this.toastService.showErrorMessage(
            'Document is niet beschikbaar.' + error,
          );
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Bezwaar-${naam}-${aanslagnummer}-${aanslagjaar}-${formattedDate}.pdf`;

        document.body.appendChild(link);
        link.click();
        this.toastService.showSuccessMessage('Document is beschikbaar');
        this.dialogVisibleChange.emit(true);
      },
      error: (error) => {
        console.log('error', error);
        this.toastService.showErrorMessage(
          'Document is niet beschikbaar  ' + error,
        );
      },
    });
  }

  dataURItoBlob(dataURI: string) {
    if (dataURI != null) {
      const byteString = atob(dataURI.replace(/"/g, ''));
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const int8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([int8Array], {type: 'application/zip'});
      return blob;
    } else throw 'No dataobject';
  }
}
